import React from 'react';

/**
 * WheelModal - Modal component for creating, viewing, and editing wheels
 * Handles: create mode, view mode, edit mode, delete confirmation, create pallet confirmation
 */
const WheelModal = ({
  isOpen,
  onClose,
  modalMode,
  selectedWheel,
  // Form state
  wheelItems,
  onRemoveItem,
  onUpdateQuantity,
  onAddItem,
  itemSearch,
  setItemSearch,
  showItemDropdown,
  setShowItemDropdown,
  filteredItems,
  onSelectItem,
  selectedItemId,
  quantity,
  setQuantity,
  lotNumber,
  setLotNumber,
  lotDate,
  setLotDate,
  dateToJulian,
  customerSearch,
  setCustomerSearch,
  showCustomerDropdown,
  setShowCustomerDropdown,
  filteredCustomers,
  onSelectCustomer,
  selectedCustomerId,
  labelText,
  setLabelText,
  labelColor,
  setLabelColor,
  isFull,
  setIsFull,
  // Calculations
  calculateTotalWeight,
  calculateTotalCases,
  // Actions
  saving,
  onSaveWheel,
  onSwitchToEdit,
  // Delete
  showDeleteConfirm,
  setShowDeleteConfirm,
  onDeleteWheel,
  // Create pallet
  showCreatePalletConfirm,
  setShowCreatePalletConfirm,
  onCreatePalletFromWheel,
  creatingPallet,
  // Move to existing pallet
  showMoveToExisting,
  setShowMoveToExisting,
  pallets,
  palletSearch,
  setPalletSearch,
  onMoveToExistingPallet,
  movingToPallet,
  // Scanner
  onOpenItemScanner
}) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal wheel-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>
              {modalMode === 'create' && 'Create New Wheel'}
              {modalMode === 'edit' && `Edit Wheel ${selectedWheel?.wheel_number}`}
              {modalMode === 'view' && `Wheel ${selectedWheel?.wheel_number}`}
            </h2>
            <div className="header-badges">
              {selectedWheel?.is_full && (
                <span className="full-badge">FULL</span>
              )}
              {selectedWheel?.label_text && (
                <span
                  className="label-badge-header"
                  style={{ backgroundColor: selectedWheel.label_color || '#757575' }}
                >
                  {selectedWheel.label_text}
                </span>
              )}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body invoice-style">
          {modalMode !== 'view' ? (
            <WheelForm
              wheelItems={wheelItems}
              onRemoveItem={onRemoveItem}
              onUpdateQuantity={onUpdateQuantity}
              onAddItem={onAddItem}
              itemSearch={itemSearch}
              setItemSearch={setItemSearch}
              showItemDropdown={showItemDropdown}
              setShowItemDropdown={setShowItemDropdown}
              filteredItems={filteredItems}
              onSelectItem={onSelectItem}
              selectedItemId={selectedItemId}
              quantity={quantity}
              setQuantity={setQuantity}
              lotNumber={lotNumber}
              setLotNumber={setLotNumber}
              lotDate={lotDate}
              setLotDate={setLotDate}
              dateToJulian={dateToJulian}
              customerSearch={customerSearch}
              setCustomerSearch={setCustomerSearch}
              showCustomerDropdown={showCustomerDropdown}
              setShowCustomerDropdown={setShowCustomerDropdown}
              filteredCustomers={filteredCustomers}
              onSelectCustomer={onSelectCustomer}
              selectedCustomerId={selectedCustomerId}
              labelText={labelText}
              setLabelText={setLabelText}
              labelColor={labelColor}
              setLabelColor={setLabelColor}
              isFull={isFull}
              setIsFull={setIsFull}
              calculateTotalWeight={calculateTotalWeight}
              calculateTotalCases={calculateTotalCases}
              onOpenItemScanner={onOpenItemScanner}
            />
          ) : (
            <WheelViewContent
              selectedWheel={selectedWheel}
              wheelItems={wheelItems}
              calculateTotalWeight={calculateTotalWeight}
              calculateTotalCases={calculateTotalCases}
            />
          )}
        </div>

        <div className="modal-footer">
          {(modalMode === 'edit' || modalMode === 'view') && (
            <button
              className="modal-btn delete"
              onClick={() => setShowDeleteConfirm(true)}
            >
              Delete
            </button>
          )}
          <button className="modal-btn close" onClick={onClose}>
            {modalMode === 'view' ? 'Close' : 'Cancel'}
          </button>
          {modalMode === 'view' && wheelItems.length > 0 && (
            <button
              className="modal-btn create-pallet"
              onClick={() => setShowCreatePalletConfirm(true)}
            >
              Create Pallet
            </button>
          )}
          {modalMode === 'view' && (
            <button
              className="modal-btn edit"
              onClick={onSwitchToEdit}
            >
              Edit
            </button>
          )}
          {modalMode !== 'view' && (
            <button
              className="modal-btn save"
              onClick={onSaveWheel}
              disabled={saving || wheelItems.length === 0}
            >
              {saving ? 'Saving...' : (modalMode === 'create' ? 'Create Wheel' : 'Update Wheel')}
            </button>
          )}
        </div>

        {showDeleteConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-dialog">
              <h3>Delete Wheel?</h3>
              <p>Are you sure you want to delete wheel "{selectedWheel?.wheel_number}"?</p>
              <p className="warning-text">This action cannot be undone.</p>
              <div className="delete-confirm-actions">
                <button
                  className="cancel-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  className="confirm-delete-btn"
                  onClick={onDeleteWheel}
                >
                  Yes, Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showCreatePalletConfirm && !showMoveToExisting && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-dialog create-pallet-dialog">
              <button
                className="dialog-close-btn"
                onClick={() => setShowCreatePalletConfirm(false)}
                disabled={creatingPallet || movingToPallet}
              >×</button>
              <h3>Move Wheel to Pallet</h3>
              <p>What would you like to do with the items from wheel "{selectedWheel?.wheel_number}"?</p>
              <div className="wheel-pallet-options">
                <button
                  className="wheel-option-btn new-pallet"
                  onClick={onCreatePalletFromWheel}
                  disabled={creatingPallet || movingToPallet}
                >
                  {creatingPallet ? 'Creating...' : 'Create New Pallet'}
                </button>
                <button
                  className="wheel-option-btn existing-pallet"
                  onClick={() => setShowMoveToExisting(true)}
                  disabled={creatingPallet || movingToPallet || !pallets || pallets.length === 0}
                >
                  Move to Existing Pallet
                </button>
              </div>
              <button
                className="cancel-btn"
                onClick={() => setShowCreatePalletConfirm(false)}
                disabled={creatingPallet || movingToPallet}
                style={{ marginTop: '12px', width: '100%' }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {showMoveToExisting && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-dialog create-pallet-dialog">
              <button
                className="dialog-close-btn"
                onClick={() => setShowMoveToExisting(false)}
                disabled={movingToPallet}
              >×</button>
              <h3>Select Pallet</h3>
              <p>Choose a pallet to add items from wheel "{selectedWheel?.wheel_number}":</p>
              <input
                type="text"
                value={palletSearch}
                onChange={(e) => setPalletSearch(e.target.value)}
                placeholder="Search pallets..."
                className="pallet-picker-search"
              />
              <div className="pallet-picker-list">
                {(pallets || [])
                  .filter(p => p.status === 'active')
                  .filter(p => {
                    if (!palletSearch) return true;
                    const s = palletSearch.toLowerCase();
                    return p.pallet_number?.toLowerCase().includes(s) ||
                           p.customer_name?.toLowerCase().includes(s) ||
                           p.item_names?.toLowerCase().includes(s);
                  })
                  .map(p => (
                    <button
                      key={p.id}
                      className="pallet-picker-item"
                      onClick={() => onMoveToExistingPallet(p.id)}
                      disabled={movingToPallet}
                    >
                      <strong>{p.pallet_number}</strong>
                      <span>{p.customer_name || 'No customer'}</span>
                      <span className="pallet-picker-meta">
                        {p.item_count} items | {parseFloat(p.total_weight || 0).toFixed(1)} kg
                      </span>
                    </button>
                  ))
                }
                {(pallets || []).filter(p => p.status === 'active').length === 0 && (
                  <p className="no-pallets-msg">No active pallets found</p>
                )}
              </div>
              <button
                className="cancel-btn"
                onClick={() => setShowMoveToExisting(false)}
                disabled={movingToPallet}
                style={{ marginTop: '12px', width: '100%' }}
              >
                {movingToPallet ? 'Moving...' : 'Back'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * WheelForm - Create/Edit form for wheels
 */
const WheelForm = ({
  wheelItems,
  onRemoveItem,
  onUpdateQuantity,
  onAddItem,
  itemSearch,
  setItemSearch,
  showItemDropdown,
  setShowItemDropdown,
  filteredItems,
  onSelectItem,
  selectedItemId,
  quantity,
  setQuantity,
  lotNumber,
  setLotNumber,
  lotDate,
  setLotDate,
  dateToJulian,
  customerSearch,
  setCustomerSearch,
  showCustomerDropdown,
  setShowCustomerDropdown,
  filteredCustomers,
  onSelectCustomer,
  labelText,
  setLabelText,
  labelColor,
  setLabelColor,
  isFull,
  setIsFull,
  calculateTotalWeight,
  calculateTotalCases,
  onOpenItemScanner
}) => {
  return (
    <div className="invoice-form">
      {/* Customer Row - Top */}
      <div className="invoice-header-row">
        <div className="invoice-customer">
          <label>Customer (Optional)</label>
          <div className="invoice-search-wrap customer-search-container">
            <input
              type="text"
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.target.value);
                setShowCustomerDropdown(true);
              }}
              onFocus={() => setShowCustomerDropdown(true)}
              placeholder="Search customer..."
            />
            {showCustomerDropdown && filteredCustomers.length > 0 && (
              <div className="invoice-dropdown">
                {filteredCustomers.map(customer => (
                  <div
                    key={customer.id}
                    className="invoice-dropdown-item"
                    onClick={() => onSelectCustomer(customer)}
                  >
                    <strong>{customer.name}</strong>
                    <span>{customer.city}, {customer.state}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Label, Color & Full Badge Options */}
      <div className="pallet-options-row">
        <div className="option-group label-group">
          <label>Label</label>
          <input
            type="text"
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            placeholder="e.g. Frozen, Rush..."
            maxLength="20"
          />
        </div>
        <div className="option-group color-group">
          <label>Color</label>
          <input
            type="color"
            value={labelColor || '#757575'}
            onChange={(e) => setLabelColor(e.target.value)}
            className="color-input"
          />
        </div>
        <div className="option-group full-group">
          <label>
            <input
              type="checkbox"
              checked={isFull}
              onChange={(e) => setIsFull(e.target.checked)}
            />
            Mark as Full
          </label>
        </div>
      </div>

      {/* Line Items Table */}
      <div className="invoice-table-wrap">
        <table className="invoice-table">
          <thead>
            <tr>
              <th className="col-item">Item</th>
              <th className="col-qty">Qty</th>
              <th className="col-lot">Lot #</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {wheelItems.map((item, index) => (
              <tr key={index}>
                <td className="col-item">
                  <span className="item-part">{item.part_number}</span>
                  <span className="item-name">{item.name}</span>
                </td>
                <td className="col-qty">
                  <input
                    type="number"
                    value={item.quantity}
                    onChange={(e) => onUpdateQuantity(index, e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                    onBlur={(e) => onUpdateQuantity(index, Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                  />
                </td>
                <td className="col-lot">{item.lot_number}</td>
                <td className="col-action">
                  <button className="invoice-remove-btn" onClick={() => onRemoveItem(index)}>×</button>
                </td>
              </tr>
            ))}
            {/* Add Item Row */}
            <tr className="add-row">
              <td className="col-item">
                <div className="invoice-item-search item-search-container">
                  <input
                    type="text"
                    value={itemSearch}
                    onChange={(e) => {
                      setItemSearch(e.target.value);
                      setShowItemDropdown(true);
                    }}
                    onFocus={() => setShowItemDropdown(true)}
                    placeholder="Search item..."
                  />
                  <button type="button" className="invoice-scan-btn" onClick={onOpenItemScanner} title="Scan">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                      <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                    </svg>
                  </button>
                  {showItemDropdown && filteredItems.length > 0 && (
                    <div className="invoice-dropdown">
                      {filteredItems.slice(0, 30).map(item => (
                        <div
                          key={item.id}
                          className="invoice-dropdown-item"
                          onClick={() => onSelectItem(item)}
                        >
                          <strong>{item.name}</strong>
                          <span>{item.part_number}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </td>
              <td className="col-qty">
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                  onBlur={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1"
                />
              </td>
              <td className="col-lot">
                <div className="invoice-lot-input">
                  <input
                    type="text"
                    value={lotNumber}
                    onChange={(e) => setLotNumber(e.target.value)}
                    placeholder="0546"
                    maxLength="4"
                  />
                  <label className="lot-date-btn">
                    📅
                    <input
                      type="date"
                      value={lotDate}
                      onChange={(e) => {
                        setLotDate(e.target.value);
                        setLotNumber(dateToJulian(e.target.value));
                      }}
                    />
                  </label>
                </div>
              </td>
              <td className="col-action">
                <button
                  className="invoice-add-btn"
                  onClick={onAddItem}
                  disabled={!selectedItemId}
                >
                  +
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Mobile Form View */}
      <div className="mobile-items-form">
        {wheelItems.map((item, index) => (
          <div key={index} className="mobile-item-card">
            <div className="item-info">
              <span className="item-part">{item.part_number}</span>
              <span className="item-name">{item.name}</span>
            </div>
            <div className="item-details">
              <span>×{item.quantity}</span>
              <span>{item.lot_number}</span>
            </div>
            <button className="remove-btn" onClick={() => onRemoveItem(index)}>×</button>
          </div>
        ))}

        <div className="mobile-add-form">
          <div className="form-row">
            <label>Item</label>
            <div className="invoice-item-search">
              <input
                type="text"
                value={itemSearch}
                onChange={(e) => {
                  setItemSearch(e.target.value);
                  setShowItemDropdown(true);
                }}
                onFocus={() => setShowItemDropdown(true)}
                placeholder="Search item..."
              />
              <button type="button" className="invoice-scan-btn" onClick={onOpenItemScanner} title="Scan">
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M2 4h2v16H2V4zm4 0h1v16H6V4zm3 0h2v16H9V4zm4 0h1v16h-1V4zm3 0h1v16h-1V4zm3 0h2v16h-2V4z"/>
                </svg>
              </button>
              {showItemDropdown && filteredItems.length > 0 && (
                <div className="invoice-dropdown">
                  {filteredItems.slice(0, 10).map(item => (
                    <div
                      key={item.id}
                      className="invoice-dropdown-item"
                      onClick={() => onSelectItem(item)}
                    >
                      <strong>{item.name}</strong>
                      <span>{item.part_number}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="row-flex">
            <div className="form-row">
              <label>Qty</label>
              <input
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value === '' ? '' : parseInt(e.target.value) || '')}
                min="1"
              />
            </div>
            <div className="form-row">
              <label>Lot #</label>
              <input
                type="text"
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                placeholder="0546"
                maxLength="4"
              />
            </div>
          </div>
          <button
            className="add-btn"
            onClick={onAddItem}
            disabled={!selectedItemId}
          >
            + Add Item
          </button>
        </div>
      </div>

      {/* Totals Row */}
      <div className="invoice-totals">
        <div className="invoice-total-row">
          <span>Items: {wheelItems.length}</span>
          <span>Cases: {calculateTotalCases()}</span>
          <span className="total-weight">Total: {calculateTotalWeight().toFixed(2)} kg</span>
        </div>
      </div>
    </div>
  );
};

/**
 * WheelViewContent - View-only display of wheel details
 */
const WheelViewContent = ({
  selectedWheel,
  wheelItems,
  calculateTotalWeight,
  calculateTotalCases
}) => {
  return (
    <div className="view-pallet-info">
      <div className="view-info-grid">
        {selectedWheel?.customer_name && (
          <div className="view-info-item">
            <label>Customer</label>
            <span>{selectedWheel.customer_name}</span>
          </div>
        )}
        {selectedWheel?.notes && (
          <div className="view-info-item full-width">
            <label>Notes</label>
            <span>{selectedWheel.notes}</span>
          </div>
        )}
      </div>

      {/* View Items Table */}
      <table className="view-items-table">
        <thead>
          <tr>
            <th>Part #</th>
            <th>Name</th>
            <th>Lot</th>
            <th>Qty</th>
            <th>Weight</th>
          </tr>
        </thead>
        <tbody>
          {wheelItems.map((item, index) => (
            <tr key={index}>
              <td>{item.part_number}</td>
              <td>{item.name}</td>
              <td>{item.lot_number}</td>
              <td>{item.quantity}</td>
              <td>{parseFloat(item.subtotal_weight || 0).toFixed(2)} kg</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan="3"><strong>TOTAL</strong></td>
            <td><strong>{calculateTotalCases()}</strong></td>
            <td><strong>{calculateTotalWeight().toFixed(2)} kg</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default WheelModal;
